import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Plus, CheckCircle, ArrowRight, Storefront } from '@phosphor-icons/react';
import {
  PosLogo,
  BuyButtonLogo,
  AffiliatesLogo,
} from '../icons/BrandIcons';
import { MERCHANT_ROUTES } from '@dukene/shared';

function AssetIcon({ src, alt, className }: { src: string; alt: string; className?: string }) {
  return <img src={src} alt={alt} className={className} />;
}

export interface SalesChannelOption {
  id: string;
  title: string;
  description: string;
  logos: React.ReactNode;
  href: string;
}

interface AddSalesChannelModalProps {
  isOpen: boolean;
  onClose: () => void;
  connectedChannelIds?: string[];
  onAddChannel?: (channel: SalesChannelOption) => void;
}

export const RECOMMENDED_SALES_CHANNELS: SalesChannelOption[] = [
  {
    id: 'online_store',
    title: 'Online Store',
    description: 'Hosted e-commerce storefront with customizable themes, SEO, and lightning-fast checkout.',
    logos: <Storefront weight="fill" className="w-5 h-5 text-[#006838]" />,
    href: '/store',
  },
  {
    id: 'google',
    title: 'Google & YouTube',
    description: 'Reach shoppers across Google Shopping, Search, and YouTube with a free synchronized product feed.',
    logos: <AssetIcon src="/icons/google-icon.svg" alt="Google" className="w-5 h-5" />,
    href: MERCHANT_ROUTES.GOOGLE_COMMERCE,
  },
  {
    id: 'meta',
    title: 'Facebook & Instagram',
    description: 'Sell across Facebook Shop and Instagram Shopping. Tag products in reels, stories, and live videos.',
    logos: (
      <div className="flex items-center -space-x-1">
        <AssetIcon src="/icons/facebook.svg" alt="Facebook" className="w-5 h-5 z-10 relative" />
        <AssetIcon src="/icons/ig-instagram-icon.svg" alt="Instagram" className="w-5 h-5" />
      </div>
    ),
    href: MERCHANT_ROUTES.META_COMMERCE,
  },
  {
    id: 'whatsapp',
    title: 'WhatsApp Catalog',
    description: 'Turn WhatsApp conversations into checkouts. Shoppable catalogs, automated carts, and broadcast deals.',
    logos: <AssetIcon src="/icons/whatsapp-icon.svg" alt="WhatsApp" className="w-5 h-5" />,
    href: MERCHANT_ROUTES.WHATSAPP_CATALOG,
  },
  {
    id: 'pos',
    title: 'Point of Sale (POS)',
    description: 'Accept in-person payments at retail, pop-ups, and markets with real-time inventory sync.',
    logos: <PosLogo className="w-5 h-5" />,
    href: '/pos',
  },
  {
    id: 'buy_button',
    title: 'Buy Button',
    description: 'Embed product cards and checkout buttons on any blog, site, or landing page in seconds.',
    logos: <BuyButtonLogo className="w-5 h-5" />,
    href: '/buy-button',
  },
  {
    id: 'affiliates',
    title: 'Affiliates & Creator Network',
    description: 'Scale through commission-based influencers. Auto-generate tracking links and pay out instantly.',
    logos: <AffiliatesLogo className="w-5 h-5" />,
    href: '/affiliates',
  },
];

export default function AddSalesChannelModal({
  isOpen,
  onClose,
  connectedChannelIds = [],
  onAddChannel,
}: AddSalesChannelModalProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleAdd = (channel: SalesChannelOption) => {
    if (onAddChannel) {
      onAddChannel(channel);
    } else {
      onClose();
      navigate(channel.href);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-in fade-in duration-150"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[520px] bg-white dark:bg-[#1A1D21] rounded-xl shadow-xl border border-[#D2D5D9] dark:border-slate-800 flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-150 overflow-hidden"
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-[#E1E3E5] dark:border-slate-800 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-[#202223] dark:text-white">
              Add sales channel
            </h2>
            <p className="text-xs text-[#5C5F62] dark:text-slate-400 mt-0.5">
              Install native sales channels to reach customers
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[#5C5F62] hover:text-[#202223] dark:hover:text-white hover:bg-[#F6F6F7] dark:hover:bg-slate-800 transition-colors"
            aria-label="Close"
          >
            <X weight="bold" className="w-4 h-4" />
          </button>
        </div>

        {/* Channels List */}
        <div className="overflow-y-auto flex-1 divide-y divide-[#E1E3E5] dark:divide-slate-800">
          {RECOMMENDED_SALES_CHANNELS.map((channel) => {
            const isConnected = connectedChannelIds.includes(channel.id);
            return (
              <div
                key={channel.id}
                className="px-5 py-3.5 flex items-center justify-between gap-4 hover:bg-[#F6F6F7]/60 dark:hover:bg-slate-800/40 transition-colors"
              >
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <div className="w-9 h-9 rounded-lg border border-[#E1E3E5] dark:border-slate-700 bg-white dark:bg-slate-900 flex items-center justify-center shrink-0 shadow-2xs mt-0.5">
                    {channel.logos}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-xs font-semibold text-[#202223] dark:text-white truncate">
                      {channel.title}
                    </h3>
                    <p className="text-xs text-[#5C5F62] dark:text-slate-400 leading-relaxed mt-0.5">
                      {channel.description}
                    </p>
                  </div>
                </div>

                <div className="shrink-0">
                  {isConnected ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border border-emerald-200/80 dark:border-emerald-800/60">
                      <CheckCircle weight="fill" className="w-3.5 h-3.5" />
                      Installed
                    </span>
                  ) : (
                    <button
                      onClick={() => handleAdd(channel)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#202223] dark:bg-white text-white dark:text-slate-900 hover:opacity-90 transition-opacity shadow-2xs"
                    >
                      <Plus weight="bold" className="w-3.5 h-3.5" />
                      Install
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-[#E1E3E5] dark:border-slate-800 bg-[#F6F6F7]/50 dark:bg-slate-900/50 flex items-center justify-between">
          <button
            onClick={() => {
              onClose();
              navigate(MERCHANT_ROUTES.SALES_CHANNELS);
            }}
            className="text-xs font-semibold text-[#202223] dark:text-slate-300 hover:underline inline-flex items-center gap-1"
          >
            Manage sales channels
            <ArrowRight weight="bold" className="w-3 h-3" />
          </button>

          <button
            onClick={onClose}
            className="px-3.5 py-1.5 rounded-lg text-xs font-semibold border border-[#D2D5D9] dark:border-slate-700 text-[#202223] dark:text-slate-300 hover:bg-[#E1E3E5]/50 dark:hover:bg-slate-800 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
